/**
 * Web component: Doogma engraving preview (doogma-engraving-preview)
 * -----------------------------------------------------------------------------
 * Drawer for previewing engraving.
 *
 */

/**
 * Create new <doogma-engraving-preview> custom element.
 */
class DoogmaEngravingPreview extends HTMLElement {
  /**
   * Custom element constructor.
   * - Always call super first in constructor.
   */
  constructor() {
    super();
    this._isPdpAddFlow = false;
    this.atcButton = this.querySelector('[js-doogma-engraving-preview="atc"]');
    this.atcButtonText = this.atcButton.querySelector("span");
    this.closeButtons = [
      ...this.querySelectorAll('[js-doogma-engraving-preview="close"]'),
    ];
    this.editButtons = [
      ...this.querySelectorAll('[js-doogma-engraving-preview="edit"]'),
    ];
    this.overlay = this.querySelector(
      '[js-doogma-engraving-preview="overlay"]',
    );
    this.price = this.querySelector('[js-doogma-engraving-preview="price"]');
    this.doogmaPreviewFooterForm = document.querySelector(
      ".doogma-engraving-preview__footer form",
    );
    this.frontPreview = {
      container: this.querySelector('[js-doogma-engraving-preview="front"]'),
      image: this.querySelector('[js-doogma-engraving-preview="front-image"]'),
      text: this.querySelector('[js-doogma-engraving-preview="front-text"]'),
    };

    this.cartDrawerPreviouslyOpen = false;
    this.loadDesign = null;
    this.currentExperienceType = null;
    this._preserveEditStateOnClose = false;
    this._hideEditBtn = false;
    this.cart =
      document.querySelector("cart-notification") ||
      document.querySelector("cart-drawer");
    this.form = this.querySelector("form");
  }

  /**
   * Lifecycle hook - component is attached to the DOM.
   */
  connectedCallback() {
    document.addEventListener("click", (event) => {
      const trigger = event.target?.closest(
        '[js-doogma-engraving-preview="open"]',
      );
      if (!trigger) return;
      const editBtn = document.querySelector(
        '[js-doogma-engraving-preview="edit"]',
      );
      this.open(trigger);
      const productInfo = trigger.closest("product-info");
      if (!productInfo) {
        this.createTriggerBtninBody(trigger);
      }
      if (editBtn) editBtn.style.display = "flex";
    });

    this.addEventListener("keyup", (event) => {
      if (event.code === "Escape") {
        this.close();
        const iframe = document.getElementById("doogma-experience-iframe");
        if (iframe && iframe.style.display !== "none") {
          if (iframe.contentWindow?.doogmaOutput) {
            window.doogmaOutput = iframe.contentWindow.doogmaOutput;
          }
        }
      }
    });

    this.form.addEventListener("submit", this.onSubmitHandler.bind(this));

    if (this.closeButtons?.length) {
      this.closeButtons.forEach((element) => {
        element.addEventListener("click", this.close.bind(this));
      });
    }

    if (this.editButtons?.length) {
      this.editButtons.forEach((element) => {
        element.addEventListener("click", this.edit.bind(this));
      });
    }

    if (this.overlay) {
      this.overlay.addEventListener("click", this.close.bind(this));
    }
  }

  createTriggerBtninBody(trigger) {
    document
      .querySelectorAll("[data-doogma-clone]")
      .forEach((el) => el.remove());
    const clone = trigger.cloneNode(true);
    clone.setAttribute("data-doogma-clone", "true");
    clone.setAttribute("js-doogma-engraving-preview", "open");
    clone.setAttribute("aria-hidden", "true");
    clone.setAttribute("tabindex", "-1");
    clone.style.display = "none";
    document.body.appendChild(clone);
  }

  /**
   * Open drawer.
   * - Must be triggered by a button with suitable data attributes.
   * @param {HTMLElement} trigger - Element which triggered drawer.
   */
  open(trigger) {
    const cartDrawer = document.querySelector("cart-drawer");
    const editBtn = document.querySelector(
      '[js-doogma-engraving-preview="edit"]',
    );
    if (cartDrawer?.classList.contains("active")) {
      cartDrawer.close();
      this.cartDrawerPreviouslyOpen = true;
    }

    this.activeElement = trigger;
    setTimeout(this.classList.add("animate", "active"));

    /**
     * Show ATC only for edit flow. Preview opened directly from cart/PDP
     * should not show ATC.
     */
    if (this.atcButton) {
      const shouldShowAtc = Boolean(window.doogmaConfiguration?.isEdit);
      this.atcButton.style.display = shouldShowAtc ? "flex" : "none";
      editBtn.style.display = !shouldShowAtc ? "flex" : "none";
    }

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn =
          document.getElementById("EngravingPreview");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
    this.updateTemplate(trigger);
  }

  /**
   * Update template.
   * @param {HTMLElement} trigger - Element which triggered drawer.
   */
  updateTemplate(trigger) {
    if (!trigger) {
      return;
    }

    /**
     * Store the experience type so edit() can load the right builder globally.
     */
    this.currentExperienceType =
      trigger.dataset.location === "cart"
        ? trigger.dataset.type || "engraving-single-2"
        : trigger.closest("doogma-engraving")?.dataset.engravingType ||
          document
            .querySelector("doogma-experience")
            ?.getAttribute("html-url")
            ?.match(/jewells\/([^/]+)\//)?.[1] ||
          "engraving-single-2";

    if (trigger.dataset.location === "cart" && trigger.dataset.savedDesign) {
      /**
       * Parse the saved-design URL to extract projectId and jsonId for loadDesign.
       * URL format: ?navco-sd={configId}--{jsonId}
       * projectId = navco-sd-{configId}, jsonId = {uuid}
       */
      const savedDesignUrl =
        trigger.dataset.savedDesign ||
        window.doogmaOutput?.navco?.result?.customLinks?.find(
          (l) => l.name === "saved-design",
        )?.url;

      if (savedDesignUrl) {
        try {
          const navcoSd = savedDesignUrl;
          if (navcoSd) {
            const strArr = navcoSd.split("navco-sd=");
            const configId = trigger.dataset.type || "engraving-single-2";
            const jsonId = strArr[1];
            this.loadDesign = {
              projectId: `navco-sd-${configId}`,
              jsonId: jsonId,
            };
          }
        } catch (err) {
          console.warn(
            "doogma-engraving-preview: could not parse saved design URL",
            err,
          );
        }
      }

      /**
       * When opening from cart drawer, rebuild window.doogmaInput.preselect
       * from the trigger's data attributes so the modal has the correct config.
       */
      window.doogmaInput = window.doogmaInput || {};
      window.doogmaInput.preselect = {
        ...(trigger.dataset.maxcharacters1
          ? { md_maxchars1: Number(trigger.dataset.maxcharacters1) }
          : {}),
        ...(trigger.dataset.maxcharacters2
          ? { md_maxchars2: Number(trigger.dataset.maxcharacters2) }
          : {}),
        ...(trigger.dataset.maxcharacters3
          ? { md_maxchars3: Number(trigger.dataset.maxcharacters3) }
          : {}),
        ...(trigger.dataset.layouttext
          ? { md_layout_text: trigger.dataset.layouttext }
          : {}),
        ...(trigger.dataset.layoutsymbol
          ? { md_layout_symbol: trigger.dataset.layoutsymbol }
          : {}),
        ...(trigger.dataset.layoutsymboltext
          ? { md_layout_symboltext: trigger.dataset.layoutsymboltext }
          : {}),
      };

      window.doogmaOutput = undefined;

      /**
       * Apply loadDesign so the modal opens with pre-populated saved design data.
       */
      if (this.loadDesign) {
        window.doogmaInput = window.doogmaInput || { preselect: {} };
        window.doogmaInput.loadDesign = this.loadDesign;
      }
    }

    /**
     * Build preview details from trigger data attributes.
     * Cart drawer triggers have: data-savedpreviewimage, data-dp-text1, data-dp-font, etc.
     * PDP triggers have: data-front-savedpreviewimage, data-front-text, data-front-font
     */
    const image =
      trigger.dataset.savedpreviewimage ||
      trigger.dataset.frontSavedpreviewimage;
    const text = trigger.dataset.dpText1 || trigger.dataset.frontText || "";
    const text2 = trigger.dataset.dpText2 || "";
    const font = (
      trigger.dataset.dpFont ||
      trigger.dataset.frontFont ||
      ""
    ).replace("pf_", "");
    const displayText = text2 ? `${text} / ${text2}` : text;

    const details = {
      image: image,
      font: font,
      text: displayText,
    };

    this.price.innerText = trigger.dataset.price || "";
    this.classList.toggle(
      "doogma-engraving-preview--cart",
      trigger.dataset.location === "cart",
    );

    this.updatePreviewTemplate(this.frontPreview, details);
  }

  /**
   * Update preview template.
   * @param {Object} elements - Elements to update.
   * @param {Object} data - Engraving data.
   */
  updatePreviewTemplate(elements, data) {
    elements.container?.classList?.toggle(
      "is-active",
      Boolean(data.image || data.text),
    );
    /**
     * Set src to blank to clear the image.
     */
    elements.image.src = "";
    elements.image.src = data.image ? data.image : "";
    elements.text.innerText = data.text ? data.text : "";

    /**
     * Update font classes.
     */
    window.doogmaEngravingFonts.forEach((font) => {
      const className = `doogma-font-${font.split(".")[0]}`;
      elements.text.classList.remove(className);
    });

    if (data.font) {
      elements.text.classList.add(`doogma-font-${data.font}`);
    }
  }

  /**
   * Close drawer.
   */
  close() {
    this.classList.remove("active");

    // Do not restore focus to hidden cloned triggers; this can scroll the page.
    const shouldRestoreFocus =
      this.activeElement &&
      !this.activeElement.hasAttribute("data-doogma-clone");
    window.removeTrapFocus(shouldRestoreFocus ? this.activeElement : null);

    document.body.classList.remove("overflow-hidden");

    // Remove body overlay added by PDP open or edit flow.
    const bodyOverlay = document.getElementById("doogma-loading-overlay");
    if (bodyOverlay) bodyOverlay.remove();

    // Reset edit state on normal close so ATC stays hidden on next plain preview open.
    window.doogmaConfiguration = window.doogmaConfiguration || {};
    if (!this._preserveEditStateOnClose) {
      window.doogmaConfiguration.isEdit = false;
    }

    /**
     * Re-open cart drawer if previously open.
     */
    if (!this.cartDrawerPreviouslyOpen) {
      return;
    }

    this.cartDrawerPreviouslyOpen = false;
    const cartDrawer = document.querySelector("cart-drawer");
    cartDrawer.open();
  }

  /**
   * Ensure a single full-page overlay exists and is visible.
   */
  ensureBodyOverlay() {
    let overlay = document.getElementById("doogma-loading-overlay");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.id = "doogma-loading-overlay";
      document.body.appendChild(overlay);
    }

    overlay.style.cssText =
      "position:fixed;top:0;left:0;width:100%;height:100%;z-index:99;background:linear-gradient(180deg, rgba(111,110,103,0.3) 0%, rgba(0,0,0,0.3) 100%);pointer-events:all;display:block;";
    return overlay;
  }

  /**
   * Show iframe and open Doogma experience with retries to handle late SDK readiness.
   *
   * Strategy: prefer clicking the SDK's .dd-trigger button (the normal SDK entry point),
   * falling back to window.doogma.experience.open() if the button isn't present.
   * Both are checked with retries so slow CDN / cold-start page loads are handled.
   */
  openIframeExperienceWithRetry({
    iframe = null,
    maxAttempts = 60,
    retryDelay = 150,
    onFailure,
  } = {}) {
    const experienceIframe =
      iframe || document.getElementById("doogma-experience-iframe");
    if (!experienceIframe) {
      onFailure?.();
      return;
    }

    experienceIframe.style.cssText =
      "position:fixed;top:0;left:0;width:100%;height:100%;z-index:99;border:none;display:block;background:transparent;";

    const tryOpen = () => {
      const iframeDoc =
        experienceIframe.contentDocument ||
        experienceIframe.contentWindow?.document;
      if (!iframeDoc) return false;

      // Primary: click the SDK's own dd-trigger button.
      // The button is positioned off-screen (not display:none) so it has layout,
      // ensuring any offsetWidth/offsetParent visibility guards in the SDK pass.
      const triggerBtn = iframeDoc.querySelector(".dd-trigger");
      if (triggerBtn) {
        // Briefly restore to a normal position so any SDK visibility guard passes,
        // then dispatch the click, then move off-screen again.
        const prev = triggerBtn.style.cssText;
        triggerBtn.style.cssText =
          "position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;";
        triggerBtn.dispatchEvent(
          new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: iframeDoc.defaultView,
          }),
        );
        // Restore after a tick so async SDK handlers still see a laid-out element.
        setTimeout(() => {
          triggerBtn.style.cssText = prev;
        }, 100);
        return true;
      }

      // Secondary: use the programmatic API if available.
      const doogmaExperience =
        experienceIframe.contentWindow?.doogma?.experience;
      if (typeof doogmaExperience?.open === "function") {
        doogmaExperience.open();
        return true;
      }

      return false;
    };

    if (tryOpen()) return;

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;

      if (tryOpen()) {
        window.clearInterval(timer);
        return;
      }

      if (attempts >= maxAttempts) {
        window.clearInterval(timer);
        console.warn(
          "doogma-engraving-preview: experience did not become ready in time",
        );
        onFailure?.();
      }
    }, retryDelay);
  }

  /**
   * Add to cart.
   */
  async onSubmitHandler(evt) {
    if (evt) {
      evt.preventDefault();
    }

    if (this.atcButton.getAttribute("aria-disabled") === "true") return;

    // this.handleErrorMessage();

    this.atcButton.setAttribute("aria-disabled", true);
    this.atcButton.classList.add("loading");
    this.querySelector(".loading__spinner")?.classList.remove("hidden");

    const formData = new FormData(this.form);

    /**
     * Build object from formData.
     */
    const properties = {};
    const productId = Number(formData.get("group_parent"));
    const quantity = 1;
    const groupProduct = Number(formData.get("group_product"));
    const groupType = formData.get("group_type");

    Object.assign(properties, this.extractProperties(formData));

    const isCartEditFlow =
      Boolean(window.doogmaConfiguration?.isEdit) &&
      this.activeElement?.dataset?.location === "cart";
    if (isCartEditFlow) {
      await this.removeCurrentEngravingFromCart();
    }

    const body = {
      items: [
        {
          id: productId,
          properties,
          quantity,
        },
      ],
    };

    /**
     * Add group data if it exists.
     */
    if (groupProduct) {
      body.items.push({
        id: groupProduct,
        parent_id: productId,
        properties: {
          _doogma_engraving_fee: true,
        },
        quantity,
      });
    }
    body.items[0].properties["_group_parent"] = "Engraving";

    /**
     * Add section rendering data if cart component exists.
     */
    if (this.cart) {
      body.sections = this.cart
        .getSectionsToRender()
        .map((section) => section.id);
      body.sections_url = window.location.pathname;
      this.cart.setActiveElement(document.activeElement);
    }

    /**
     * Add product form product.
     */
    this.addToCart({ body: JSON.stringify(body), id: productId });
  }

  /**
   * Remove the currently edited cart line(s) before re-adding with updated engraving.
   * Uses cart updateQuantity() so section re-rendering stays consistent.
   */
  async removeCurrentEngravingFromCart() {
    const cartItemsComponent =
      document.querySelector("cart-drawer-items") ||
      document.querySelector("cart-items");
    const triggerRow = this.activeElement?.closest("tr.cart-item");

    if (!cartItemsComponent?.updateQuantity) {
      return;
    }

    const parseLine = (id) => {
      const match = id.match(/(\d+)$/);
      return match ? Number(match[1]) : null;
    };

    const lines = [];
    if (triggerRow?.id) {
      const currentLine = parseLine(triggerRow.id);
      if (currentLine) lines.push(currentLine);

      const sibling = triggerRow.previousElementSibling;
      if (sibling?.classList?.contains("cart-item")) {
        const siblingLine = parseLine(sibling.id);
        if (siblingLine) lines.push(siblingLine);
      }
    }

    // Fallback for cloned trigger buttons that are not inside cart rows.
    if (!lines.length) {
      try {
        const cartResponse = await fetch("/cart.js", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });

        if (cartResponse.ok) {
          const cartData = await cartResponse.json();
          const items = cartData?.items || [];
          const groupParentId = Number(
            this.activeElement?.dataset?.groupParentId,
          );
          const groupProductId = Number(
            this.activeElement?.dataset?.groupProductId,
          );
          const savedDesign = this.activeElement?.dataset?.savedDesign;

          let parentMatch = null;
          if (groupParentId) {
            parentMatch = items.find((item) => {
              if (Number(item.variant_id) !== groupParentId) return false;
              if (!savedDesign) return true;

              const savedDesignProperty =
                item.properties?._doogma_engraving_saved_design ||
                item.properties?._doogma_engraving_savedDesign ||
                item.properties?._doogma_engraving_saved_design_url ||
                item.properties?.["_doogma_engraving_saved-design"];
              return savedDesignProperty === savedDesign;
            });

            if (!parentMatch) {
              parentMatch = items.find(
                (item) => Number(item.variant_id) === groupParentId,
              );
            }
          }

          if (parentMatch) {
            const parentLine =
              items.findIndex((item) => item.key === parentMatch.key) + 1;
            if (parentLine > 0) lines.push(parentLine);
          }

          if (groupProductId) {
            const feeMatch = items.find((item) => {
              if (Number(item.variant_id) !== groupProductId) return false;
              if (!parentMatch?.key) return true;
              return item.parent_relationship?.parent === parentMatch.key;
            });

            if (feeMatch) {
              const feeLine =
                items.findIndex((item) => item.key === feeMatch.key) + 1;
              if (feeLine > 0) lines.push(feeLine);
            }
          }
        }
      } catch (error) {
        console.error(
          "doogma-engraving-preview: could not resolve existing cart lines before edit add",
          error,
        );
      }
    }

    const uniqueLines = [...new Set(lines)].sort((a, b) => b - a);

    for (const line of uniqueLines) {
      await cartItemsComponent.updateQuantity(
        line,
        0,
        "",
        this.activeElement?.dataset?.groupParentId,
      );
    }
  }

  /**
   * Add to cart.
   * @param {Object} body - Fetch body object.
   * @param {Boolean} [engraving] - Is engraving product?
   * @param {Number} id - Product variant ID being added.
   */
  addToCart({ body, id } = {}) {
    return new Promise((resolve, reject) => {
      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      config.body = body;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: "product-form",
              productVariantId: id,
              errors: response.errors || response.description,
              message: response.message,
            });

            // this.handleErrorMessage(response.description);

            // const soldOutMessage = this.atcButton.querySelector('.sold-out-message');

            // if (!soldOutMessage) {
            //   return;
            // }

            this.atcButton.setAttribute("aria-disabled", true);
            this.atcButtonText.classList.add("hidden");
            // this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }
          this.cart.renderContents(response);

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: id,
            cartData: response,
          });
        })
        .catch((e) => {
          console.error(e);
          reject(e);
        })
        .finally(() => {
          this.atcButton.classList.remove("loading");

          if (this.cart && this.cart.classList.contains("is-empty")) {
            this.cart.classList.remove("is-empty");
          }

          if (!this.error) {
            this.atcButton.removeAttribute("aria-disabled");
          }
          window.doogmaConfiguration.isEdit = false;
          this.close();
          this.querySelector(".loading__spinner").classList.add("hidden");
          resolve();
        });
    });
  }

  /**
   * Extract properties from FormData, returning an object keyed by property name.
   * @param {FormData} formData
   * @returns {Object}
   */
  extractProperties(formData) {
    const properties = {};
    for (const [key, value] of formData) {
      if (!key.includes("properties[")) continue;
      const property = key.replace("properties[", "").replace("]", "");
      properties[property] = value;
    }
    return properties;
  }

  /**
   * Edit engraving.
   */
  edit() {
    /**
     * Prevent close() from re-opening the cart drawer.
     */
    window.doogmaConfiguration = window.doogmaConfiguration || {};
    window.doogmaConfiguration.isEdit = true;
    this._isPdpAddFlow = false;
    this._hideEditBtn = true;
    this.cartDrawerPreviouslyOpen = false;

    // Keep edit state true while we close preview and launch editor.
    this._preserveEditStateOnClose = true;
    this.close();
    this._preserveEditStateOnClose = false;

    // Add body overlay for the edit flow (same as PDP open).
    this.ensureBodyOverlay();

    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer?.classList.contains("active")) {
      cartDrawer.close();
    }

    /**
     * Build window.doogmaInput.preselect from the trigger's data attributes
     * so the engraving experience loads with the correct config.
     */
    const trigger = this.activeElement;
    window.doogmaInput = { preselect: {} };
    if (trigger) {
      if (trigger.dataset.maxcharacters1)
        window.doogmaInput.preselect.md_maxchars1 = Number(
          trigger.dataset.maxcharacters1,
        );
      if (trigger.dataset.maxcharacters2)
        window.doogmaInput.preselect.md_maxchars2 = Number(
          trigger.dataset.maxcharacters2,
        );
      if (trigger.dataset.maxcharacters3)
        window.doogmaInput.preselect.md_maxchars3 = Number(
          trigger.dataset.maxcharacters3,
        );
      if (trigger.dataset.layouttext)
        window.doogmaInput.preselect.md_layout_text =
          trigger.dataset.layouttext;
      if (trigger.dataset.layoutsymbol)
        window.doogmaInput.preselect.md_layout_symbol =
          trigger.dataset.layoutsymbol;
      if (trigger.dataset.layoutsymboltext)
        window.doogmaInput.preselect.md_layout_symboltext =
          trigger.dataset.layoutsymboltext;
    }

    /**
     * Re-apply loadDesign to window.doogmaInput before reloading the experience
     * so the modal opens with pre-populated saved design data.
     */
    if (this.loadDesign) {
      window.doogmaInput.loadDesign = this.loadDesign;
    }

    /**
     * Load the Doogma experience for the current type (works on any page,
     * not just PDP) and open the modal once ready.
     */
    this._ensureDoogmaExperience(
      this.currentExperienceType || "engraving-single-2",
      () => {
        // Bridge has already clicked dd-trigger at the right time.
        // Just make the iframe visible so the user sees the opening experience.
        this.ensureBodyOverlay();
        const iframe = document.getElementById("doogma-experience-iframe");
        if (iframe) {
          iframe.style.cssText =
            "position:fixed;top:0;left:0;width:100%;height:100%;z-index:99999;border:none;display:block;background:transparent;";
        }
      },
    );
  }

  _handleDoogmaClosed() {
    const output = window.doogmaOutput?.navco?.result;
    if (!output) {
      const bodyOverlay = document.getElementById("doogma-loading-overlay");
      if (bodyOverlay) bodyOverlay.remove();
      document.body.classList.remove("overflow-hidden");
      return;
    }
    
    if (output.metadata?.md_submitted) {
      const isSubmitted =
        String(output.metadata?.md_submitted).toLowerCase() === "true";
      if (!isSubmitted) {
        const bodyOverlay = document.getElementById("doogma-loading-overlay");
        if (bodyOverlay) bodyOverlay.remove();
        document.body.classList.remove("overflow-hidden");
        window.doogmaConfiguration = window.doogmaConfiguration || {};
        window.doogmaConfiguration.isEdit = false;
        return;
      }
      
    }

    this.clearLineItemProperties();

    const previewImage = output.customLinks?.find(
      (itm) => itm.name === "savedPreviewImage",
    );
    const savedDesign = output.customLinks?.find(
      (itm) => itm.name === "saved-design",
    );

    let btn =
      document.querySelector('[data-doogma-clone="true"]') ||
      document.querySelector(
        'product-info [js-doogma-engraving-preview="open"]',
      );
    if (!btn) return;

    if (this._isPdpAddFlow) {
      const engravingEl = document.querySelector("doogma-engraving");
      if (engravingEl) {
        if (engravingEl.dataset.engravingType)
          btn.setAttribute("data-type", engravingEl.dataset.engravingType);
        if (engravingEl.dataset.engravingMaxChars1)
          btn.setAttribute(
            "data-maxcharacters1",
            engravingEl.dataset.engravingMaxChars1,
          );
        if (engravingEl.dataset.engravingMaxChars2)
          btn.setAttribute(
            "data-maxcharacters2",
            engravingEl.dataset.engravingMaxChars2,
          );
        if (engravingEl.dataset.engravingMaxChars3)
          btn.setAttribute(
            "data-maxcharacters3",
            engravingEl.dataset.engravingMaxChars3,
          );
        if (engravingEl.dataset.engravingLayoutText)
          btn.setAttribute(
            "data-layouttext",
            engravingEl.dataset.engravingLayoutText,
          );
        if (engravingEl.dataset.engravingLayoutSymbol)
          btn.setAttribute(
            "data-layoutsymbol",
            engravingEl.dataset.engravingLayoutSymbol,
          );
        if (engravingEl.dataset.engravingLayoutSymboltext)
          btn.setAttribute(
            "data-layoutsymboltext",
            engravingEl.dataset.engravingLayoutSymboltext,
          );
        if (engravingEl.dataset.groupProduct)
          btn.setAttribute(
            "data-group-product-id",
            engravingEl.dataset.groupProduct,
          );
        if (engravingEl.dataset.groupType)
          btn.setAttribute("data-group-type", engravingEl.dataset.groupType);
      }
    }

    // For PDP add flow, use current PDP's selected variant
    // For cart drawer edit flow, use trigger's parent ID
    if (this._isPdpAddFlow) {
      const currentVariant = window.productVariants?.find((v) => v.selected);
      if (currentVariant?.id) {
        btn.setAttribute("data-group-parent-id", currentVariant.id);
      }
    } else {
      const triggerParentId = this.activeElement?.dataset?.groupParentId;
      if (triggerParentId) {
        btn.setAttribute("data-group-parent-id", triggerParentId);
      }
    }

    if (previewImage)
      btn.setAttribute("data-savedpreviewimage", previewImage.url);
    if (savedDesign) btn.setAttribute("data-saved-design", savedDesign.url);
    output.customLinks?.forEach((link) => {
      // if (link.name === 'savedsvg1') btn.setAttribute('data-savedsvg1', link.url)
      // if (link.name === 'jewellsCartHint') btn.setAttribute('data-jewellscarthint', link.url)
      btn.setAttribute(`data-${link.name}`, link.url);
    });
    if (output.parameters?.dp_text1 !== undefined)
      btn.setAttribute("data-dp-text1", output.parameters.dp_text1 ?? "");
    if (output.parameters?.dp_text2 !== undefined)
      btn.setAttribute("data-dp-text2", output.parameters.dp_text2 ?? "");
    if (output.parameters?.dp_text3 !== undefined)
      btn.setAttribute("data-dp-text3", output.parameters.dp_text3 ?? "");
    if (output.parameters?.dp_font)
      btn.setAttribute("data-dp-font", output.parameters.dp_font);
    if (output.parameters?.dp_symbol)
      btn.setAttribute("data-dp-symbol", output.parameters.dp_symbol);
    if (output.parameters?.dp_symbol2)
      btn.setAttribute("data-dp-symbol2", output.parameters.dp_symbol2);
    if (output.parameters?.dp_scaletext)
      btn.setAttribute("data-dp-scaletext", output.parameters.dp_scaletext);

    const attrInputMap = [
      ["maxcharacters1", "maxCharacters1"],
      ["maxcharacters2", "maxCharacters2"],
      ["layouttext", "layoutText"],
      ["layoutsymbol", "layoutSymbol"],
      ["layoutsymboltext", "layoutSymbolText"],
      ["type", "type"],
      ["savedpreviewimage", "savedPreviewImage"],
      ["saved-design", "saved-design"],
      ["savedsvg1", "savedsvg1"],
      ["savedsvg2", "savedsvg2"],
      ["jewellscarthint", "jewellsCartHint"],
      ["dp-text1", "dp_text1"],
      ["dp-text2", "dp_text2"],
      ["dp-text3", "dp_text3"],
      ["dp-font", "dp_font"],
      ["dp-symbol", "dp_symbol"],
      ["dp-symbol2", "dp_symbol2"],
      ["dp-scaletext", "dp_scaletext"],
    ];
    attrInputMap.forEach(([attr, key]) => {
      const value = btn.getAttribute(`data-${attr}`);
      if (value !== null) {
        this.createInput({ key, value, property: true });
      }
    });

    const groupProductId = btn.getAttribute("data-group-product-id");
    const groupType = btn.getAttribute("data-group-type");
    const parentProductId = btn.getAttribute("data-group-parent-id");
    if (groupProductId) {
      this.createInput({ key: "group_product", value: groupProductId });
      this.createInput({ key: "group_type", value: groupType || "" });
    }
    if (parentProductId) {
      this.createInput({ key: "group_parent", value: parentProductId });
    }

    window.doogmaConfiguration = window.doogmaConfiguration || {};
    window.doogmaConfiguration.isEdit = true;
    this.open(btn);
  }

  /**
   * Create input for line item properties.
   * @param {String} key - Property key.
   * @param {String} [position] - Front or back.
   * @param {Boolean} [property] - Prepend with properties[] string.
   * @param {*} value - Property value.
   */
  createInput({ key, property = false, value } = {}) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.value = value;

    if (property) {
      input.name = `properties[_doogma_engraving_${key}]`;
    } else {
      input.name = key;
    }
    const ctaForm = this.doogmaPreviewFooterForm;
    this.inputs.push(input);
    ctaForm.insertAdjacentElement("afterbegin", input);
  }

  /**
   * Clear line item properties.
   */
  clearLineItemProperties() {
    /**
     * Remove tracked hidden inputs.
     */
    this.inputs?.forEach((element) => element?.remove());
    this.inputs = [];

    /**
     * Also remove any orphaned doogma engraving inputs directly from the form.
     * This handles cases where the component was re-created (e.g. variant swap)
     * and this.inputs was reset before cleanup.
     */
    if (this.doogmaPreviewFooterForm) {
      this.doogmaPreviewFooterForm
        .querySelectorAll('input[type="hidden"]')
        .forEach((input) => {
          if (
            input.name.startsWith("properties[_doogma_engraving_") ||
            input.name === "group_product" ||
            input.name === "group_type"
          ) {
            input.remove();
          }
        });
    }
  }

  /**
   * Ensure a <doogma-experience> element is present for the given type,
   * creating one inside a hidden iframe so each type gets its own
   * customElements registry and a truly clean environment.
   *
   * The iframe is made full-screen and visible when the modal is opened,
   * and hidden again when the modal is closed or Escape is pressed.
   *
   * @param {string} type - Experience type param (e.g. 'engraving-single-2').
   * @param {Function} onReady - Called once the experience is ready.
   */
  _ensureDoogmaExperience(type, onReady) {
    const baseUrl = `https://cdnclients.doogma.com/navco/jewells/${type}/prod/`;
    const htmlUrl = `${baseUrl}experience.html`;
    const scriptUrl = `${baseUrl}experience.js`;

    if (this._doogmaMessageHandler) {
      window.removeEventListener("message", this._doogmaMessageHandler);
      this._doogmaMessageHandler = null;
    }

    const oldIframe = document.getElementById("doogma-experience-iframe");
    if (oldIframe?.contentWindow?.doogmaOutput) {
      window.doogmaOutput = oldIframe.contentWindow.doogmaOutput;
    }
    if (oldIframe) {
      oldIframe.src = "about:blank";
      oldIframe.remove();
    }

    const iframe = document.createElement("iframe");
    iframe.id = "doogma-experience-iframe";
    // IMPORTANT: iframe MUST be display:block from creation so the SDK can
    // measure canvas dimensions. display:none causes 0x0 canvas and broken init.
    iframe.style.cssText =
      "position:fixed;top:0;left:0;width:100%;height:100%;z-index:99999;border:none;display:block;background:transparent;";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    const iframeWin = iframe.contentWindow;

    iframeWin.doogmaInput = {
      preselect: window.doogmaInput?.preselect
        ? JSON.parse(JSON.stringify(window.doogmaInput.preselect))
        : {},
      ...(window.doogmaInput?.loadDesign
        ? { loadDesign: window.doogmaInput.loadDesign }
        : {}),
    };

    iframeDoc.open();
    iframeDoc.write(
      "<!DOCTYPE html><html><head><style>body{margin:0;padding:0;}.dd-trigger{position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;overflow:hidden;pointer-events:none;}</style></head><body></body></html>",
    );
    iframeDoc.close();

    const bridgeScript = iframeDoc.createElement("script");
    bridgeScript.textContent = `
      var _doogmaClickFired = false;
      var _doogmaCloseHandled = false;
      var _capturedShadowRoot = null;
      var _popupOpenConfirmed = false;
      var _metaResetHookBound = false;

      /**
       * Intercept attachShadow to capture a reference to the closed shadow root
       * that <doogma-experience> creates. This lets us observe dd-popup state.
       */
      var _origAttachShadow = Element.prototype.attachShadow;
      Element.prototype.attachShadow = function(opts) {
        var shadow = _origAttachShadow.call(this, opts);
        if (this.tagName && this.tagName.toLowerCase() === 'doogma-experience') {
          console.log('[doogma-bridge] attachShadow intercepted on <doogma-experience> — shadow root captured');
          _capturedShadowRoot = shadow;
        }
        return shadow;
      };

      function getExperienceEvents() {
        var exp = window.doogma && window.doogma.experience;
        if (!exp || !exp.events) return null;
        return exp.events;
      }

      function emitMetaSubmittedFalse(source) {
        var events = getExperienceEvents();
        if (!events || typeof events.emit !== 'function') {
          console.log('[doogma-bridge] ' + source + ': cannot emit meta:set yet (events API unavailable)');
          return false;
        }
        try {
          events.emit('meta:set', { md_submitted: 'false' });
          console.log('[doogma-bridge] ' + source + ': emitted meta:set { md_submitted: "false" }');
          return true;
        } catch (err) {
          console.warn('[doogma-bridge] ' + source + ': failed emitting meta:set', err);
          return false;
        }
      }

      function bindPopupOpenedMetaResetHook() {
        if (_metaResetHookBound) return true;
        var events = getExperienceEvents();
        if (!events || typeof events.on !== 'function' || typeof events.emit !== 'function') {
          return false;
        }
        events.on('popup:opened', function() {
          console.log('[doogma-bridge] popup:opened received — resetting md_submitted=false');
          emitMetaSubmittedFalse('popup:opened');
        });
        _metaResetHookBound = true;
        console.log('[doogma-bridge] bound popup:opened -> meta:set reset hook');
        return true;
      }

      function ensurePopupOpenedMetaResetHook() {
        if (bindPopupOpenedMetaResetHook()) return;
        var attempts = 0;
        var maxAttempts = 40;
        var timer = setInterval(function() {
          attempts++;
          if (bindPopupOpenedMetaResetHook()) {
            clearInterval(timer);
            return;
          }
          if (attempts >= maxAttempts) {
            clearInterval(timer);
            console.warn('[doogma-bridge] popup:opened reset hook could not be bound in time');
          }
        }, 250);
      }

      /**
       * After triggering open, poll the shadow root to verify dd-popup got
       * the "open" class. If not, retry opening via the SDK API.
       */
      function ensurePopupOpen() {
        if (_popupOpenConfirmed) return;
        var attempts = 0;
        var maxAttempts = 40; // 40 * 250ms = 10s
        console.log('[doogma-bridge] ensurePopupOpen: starting poll for dd-popup.open (max ' + maxAttempts + ' attempts)');
        var timer = setInterval(function() {
          attempts++;
          var popup = null;
          var source = 'none';

          // Try captured shadow root first
          if (_capturedShadowRoot) {
            popup = _capturedShadowRoot.querySelector('.dd-popup');
            if (popup) source = 'capturedShadowRoot';
          }

          // Fallback: try open shadow root on element
          if (!popup) {
            var expEl = document.querySelector('doogma-experience');
            if (expEl && expEl.shadowRoot) {
              popup = expEl.shadowRoot.querySelector('.dd-popup');
              if (popup) source = 'element.shadowRoot';
            }
          }

          if (!popup) {
            console.log('[doogma-bridge] ensurePopupOpen attempt ' + attempts + ': dd-popup not found yet (shadowRoot source: ' + source + ')');
          } else if (popup.classList.contains('open')) {
            console.log('[doogma-bridge] ensurePopupOpen attempt ' + attempts + ': dd-popup.open CONFIRMED via ' + source + ' ✓');
            _popupOpenConfirmed = true;
            clearInterval(timer);
            return;
          } else {
            console.log('[doogma-bridge] ensurePopupOpen attempt ' + attempts + ': dd-popup found via ' + source + ' but missing "open" class — retrying open...');
            // Try SDK API
            if (window.doogma && window.doogma.experience && typeof window.doogma.experience.open === 'function') {
              console.log('[doogma-bridge] ensurePopupOpen: calling window.doogma.experience.open()');
              window.doogma.experience.open();
            } else {
              console.log('[doogma-bridge] ensurePopupOpen: SDK API not available, adding open class directly');
              popup.classList.add('open');
            }
          }

          if (attempts >= maxAttempts) {
            clearInterval(timer);
            if (popup && !popup.classList.contains('open')) {
              console.warn('[doogma-bridge] ensurePopupOpen: TIMEOUT — forcing dd-popup open class as last resort');
              popup.classList.add('open');
            } else if (!popup) {
              console.warn('[doogma-bridge] ensurePopupOpen: TIMEOUT — dd-popup never appeared in shadow root');
            }
          }
        }, 250);
      }

      /**
       * Click the SDK's own dd-trigger button from INSIDE the iframe at the
       * exact moment the experience element signals it is ready, then verify
       * dd-popup gets the open class.
       */
      function openExperience() {
        if (_doogmaClickFired) return;
        _doogmaClickFired = true;
        console.log('[doogma-bridge] openExperience: triggered');

        // Ensure this is wired once and reset stale submission state each open.
        ensurePopupOpenedMetaResetHook();
        emitMetaSubmittedFalse('before-open');

        var btn = document.querySelector('.dd-trigger');
        if (btn) {
          console.log('[doogma-bridge] openExperience: clicking .dd-trigger button');
          btn.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;overflow:hidden;';
          btn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        } else {
          console.warn('[doogma-bridge] openExperience: .dd-trigger button NOT found in iframe DOM');
        }

        // Also try the SDK API directly
        if (window.doogma && window.doogma.experience && typeof window.doogma.experience.open === 'function') {
          console.log('[doogma-bridge] openExperience: also calling window.doogma.experience.open()');
          window.doogma.experience.open();
        } else {
          console.log('[doogma-bridge] openExperience: window.doogma.experience.open not available (will rely on dd-trigger click)');
        }

        // Start observing to make sure dd-popup actually opened
        console.log('[doogma-bridge] openExperience: scheduling ensurePopupOpen check in 300ms');
        setTimeout(ensurePopupOpen, 300);

        // Tell parent the experience is opening so it can show the iframe.
        console.log('[doogma-bridge] openExperience: posting doogma:ready to parent');
        window.parent.postMessage({ type: 'doogma:ready' }, '*');
      }

      function handleDoogmaClose() {
        if (_doogmaCloseHandled) return;
        _doogmaCloseHandled = true;
        _popupOpenConfirmed = false;
        console.log('[doogma-bridge] handleDoogmaClose: doogma close event received, posting to parent');
        if (window.doogmaOutput) {
          window.parent.doogmaOutput = window.doogmaOutput;
        }
        window.parent.postMessage({ type: 'doogma:close' }, '*');
      }

      /**
       * Close listeners on document (bubbling).
       */
      document.addEventListener('doogma:close', handleDoogmaClose);
      document.addEventListener('doogma:closed', handleDoogmaClose);

      /**
       * postMessages from SDK's internal cross-origin experience iframe.
       */
      window.addEventListener('message', function(e) {
        if (e.data && e.data.type === 'doogma:ready') {
          console.log('[doogma-bridge] postMessage received: doogma:ready');
          openExperience();
        }
        if (e.data && (e.data.type === 'doogma:close' || e.data.type === 'doogma:closed')) {
          console.log('[doogma-bridge] postMessage received:', e.data.type);
          handleDoogmaClose();
        }
      });

      /**
       * Wait for <doogma-experience> element and attach listeners to IT.
       * The element fires doogma:ready only after it has fully initialised
       * (templates resolved, dd-popup ready).
       */
      var _expObserver = new MutationObserver(function() {
        var expEl = document.querySelector('doogma-experience');
        if (expEl && !expEl._bridgeAttached) {
          expEl._bridgeAttached = true;
          console.log('[doogma-bridge] MutationObserver: <doogma-experience> found — attaching doogma:ready listener');
          ensurePopupOpenedMetaResetHook();
          expEl.addEventListener('doogma:ready', openExperience);
          expEl.addEventListener('doogma:closed', handleDoogmaClose);
          expEl.addEventListener('doogma:close', handleDoogmaClose);
          _expObserver.disconnect();
        }
      });
      _expObserver.observe(document.documentElement, { childList: true, subtree: true });

      /**
       * Internal fallback: if the experience element never fires doogma:ready
       * (e.g. SDK version difference), attempt after 5 s.
       */
      setTimeout(function() {
        if (!_doogmaClickFired) {
          console.warn('[doogma-bridge] 5s fallback: doogma:ready never fired on <doogma-experience> — triggering openExperience anyway');
          openExperience();
        }
      }, 5000);
    `;
    iframeDoc.head.appendChild(bridgeScript);

    this._doogmaMessageHandler = (event) => {
      if (event.source !== iframeWin) return;
      if (event.data?.type === "doogma:ready") {
        /**
         * Do NOT remove the handler here — it must stay alive to receive
         * the subsequent doogma:close message.
         */
        clearTimeout(fallbackTimer);
        onReady();
      }
      if (event.data?.type === "doogma:close") {
        /**
         * Sync doogmaOutput from iframe, hide the iframe first so it's gone
         * before the preview drawer opens, then dispatch the event.
         */
        if (iframeWin.doogmaOutput) {
          window.doogmaOutput = iframeWin.doogmaOutput;
        }
        iframe.style.cssText =
          "border:none;display:none;background:transparent;";

        window.removeEventListener("message", this._doogmaMessageHandler);
        this._doogmaMessageHandler = null;

        this._handleDoogmaClosed();
      }
    };
    window.addEventListener("message", this._doogmaMessageHandler);

    const sdkScript = iframeDoc.createElement("script");
    sdkScript.src = scriptUrl;

    sdkScript.onload = () => {
      // Create elements immediately — no delay.
      // The iframe is already display:block so SDK can measure properly.
      const engravingEl = iframeDoc.createElement("doogma-engraving");
      engravingEl.setAttribute("data-engraving-type", type);

      if (window.doogmaInput?.preselect) {
        const p = window.doogmaInput.preselect;
        if (p.md_maxchars1)
          engravingEl.setAttribute(
            "data-engraving-max-chars-1",
            p.md_maxchars1,
          );
        if (p.md_maxchars2)
          engravingEl.setAttribute(
            "data-engraving-max-chars-2",
            p.md_maxchars2,
          );
        if (p.md_maxchars3)
          engravingEl.setAttribute(
            "data-engraving-max-chars-3",
            p.md_maxchars3,
          );
        if (p.md_layout_text)
          engravingEl.setAttribute(
            "data-engraving-layout-text",
            JSON.stringify(p.md_layout_text),
          );
        if (p.md_layout_symbol)
          engravingEl.setAttribute(
            "data-engraving-layout-symbol",
            JSON.stringify(p.md_layout_symbol),
          );
        if (p.md_layout_symboltext)
          engravingEl.setAttribute(
            "data-engraving-layout-symboltext",
            JSON.stringify(p.md_layout_symboltext),
          );
      }

      const expEl = iframeDoc.createElement("doogma-experience");
      expEl.setAttribute("html-url", htmlUrl);
      engravingEl.appendChild(expEl);
      iframeDoc.body.appendChild(engravingEl);
    };

    sdkScript.onerror = () => {
      console.error(
        "doogma-engraving-preview: failed to load experience script",
        scriptUrl,
      );
      onReady();
    };

    iframeDoc.head.appendChild(sdkScript);

    const fallbackTimer = setTimeout(() => {
      /**
       * Parent-side last-resort fallback. By now the bridge's own 5 s internal
       * fallback has already fired openExperience() inside the iframe.
       * Here we just ensure the iframe is visible.
       */
      onReady();
    }, 8000);
  }
}

customElements.define("doogma-engraving-preview", DoogmaEngravingPreview);
